import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Reveal, FadeIn } from "@/components/Reveal";
import CourseCard from "@/components/CourseCard";

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params] = useSearchParams();
  const [active, setActive] = useState("All");

  useEffect(() => {
    api
      .get("/courses")
      .then(({ data }) => {
        setCourses(data.courses);
        setCategories(["All", ...data.categories]);
        const cat = params.get("cat");
        if (cat && ["All", ...data.categories].includes(cat)) setActive(cat);
      })
      .catch(() => {});
  }, [params]);

  const filtered = useMemo(
    () => (active === "All" ? courses : courses.filter((c) => c.category === active)),
    [courses, active]
  );

  return (
    <div data-testid="catalog-page">
      {/* Header — bold light with blob decorations */}
      <section className="bg-paper relative overflow-hidden">
        <div className="blob-decoration w-[380px] h-[380px] bg-lemonlight -top-24 -right-24" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-10 md:pt-14 pb-10 md:pb-14 relative">
          <FadeIn>
            <p className="font-sans text-xs font-bold text-emerald mb-6 uppercase tracking-wide">
              The full catalog · {courses.length} courses · every fee indicative
            </p>
          </FadeIn>
          <h1 className="font-display font-bold tracking-tight text-5xl md:text-7xl leading-[1.0] max-w-4xl text-ink">
            Every course. Every teacher a <span className="text-terracotta">practitioner.</span>
          </h1>
          <p className="font-sans text-lg md:text-xl text-graphite mt-6 max-w-2xl leading-relaxed">
            A coding course and a dance class sit side by side here — treated exactly the
            same, because both are taught by someone who actually did the work.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-6 border-b border-line sticky top-16 md:top-20 z-30 bg-paper/90 backdrop-blur-xl">
        <div className="flex flex-wrap gap-2.5">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-sans text-sm font-bold rounded-full px-5 py-2.5 transition-colors ${
                active === cat
                  ? "bg-emerald text-white"
                  : "bg-stone text-graphite hover:bg-emeraldlight hover:text-emerald"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div key={active} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.06}>
              <CourseCard course={c} index={courses.indexOf(c)} />
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="font-sans text-sm font-semibold text-graphite py-16 text-center">
            Loading catalog…
          </p>
        )}
      </section>
    </div>
  );
}
